import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { DownloadDocumentService } from '@app/shared/service/download-document-service';
import { TimeSheetTaskWithoutDays } from '@app/time-sheet/models/time-sheet-task';
import { FileDownloadData } from '@app/upload-time-sheet/model/file-download-data';

import { ToastrService } from 'ngx-toastr';

import { UploadTimeSheetService } from '../../service/upload-time-sheet-service';

@Component({
  selector: 'abc-upload-time-sheet-card',
  imports: [],
  templateUrl: 'upload-time-sheet-card.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UploadTimeSheetCard {
  private uploadTimeSheetService = inject(UploadTimeSheetService);
  private toastrService = inject(ToastrService);
  private downloadDocumentService = inject(DownloadDocumentService);

  task = input.required<TimeSheetTaskWithoutDays>();
  year = input.required<number>();
  month = input.required<number>();

  selectedFile = signal<File | null>(null);
  isFileSelected = computed(() => this.selectedFile() !== null);

  fileInput = viewChild<ElementRef<HTMLInputElement>>('fileInput');
  uploadStatus = signal<'idle' | 'uploading' | 'success' | 'error'>('idle');

  metadata = signal<string | undefined>(undefined);

  constructor() {
    effect(() => {
      this.metadata.set(undefined);
      const currentTask = this.task();
      if (currentTask.accepted) {
        this.getMetaData();
      }
    });
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.selectedFile.set(input.files[0]);
    }
  }

  upload() {
    const file = this.selectedFile();
    if (file === null) {
      return;
    }

    this.uploadStatus.set('uploading');

    this.uploadTimeSheetService
      .uploadTimeSheet(this.year(), this.month(), this.task().projectId, file)
      .subscribe({
        next: () => {
          this.uploadStatus.set('success');
          this.toastrService.success(
            $localize`:toast|Success message for completed timesheet upload@@uploadSuccess:Timesheet uploaded successfully.`
          );
          this.selectedFile.set(null);
          const input = this.fileInput()?.nativeElement;
          if (input) {
            input.value = '';
          }

          setTimeout(() => this.uploadStatus.set('idle'), 3000);
        },
        error: () => {
          this.uploadStatus.set('error');
          this.toastrService.error(
            $localize`:toast|Error message for failed timesheet upload@@uploadError:An error occurred while uploading the timesheet.`
          );
        },
      });
  }

  getMetaData() {
    this.downloadDocumentService.getTimeSheetMetaData(this.task().timesheetId).subscribe({
      next: (response) => {
        this.metadata.set(response.fileDownloadName);
      },
    });
  }

  downloadTimeSheet() {
    this.downloadDocumentService.downloadTimeSheet(this.task().timesheetId).subscribe({
      next: (response: FileDownloadData) => {
        if (response.url) {
          window.open(response.url, '_blank');
        }
      },
    });
  }
}
